import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
// Диалог доступа к заметке: кто видит. Автор выбирает видимость
// (всем / только я / выбранные участники) и точечный список участников.
// Список заменяется целиком одним PATCH — это ответ на вопрос «кому видно
// сейчас», а не журнал выдач.
import { useEffect, useState, type ReactElement } from 'react';
import { Alert, Modal, Radio, Select } from 'antd';

import { useCurrentUser } from '../../../shared/scope/useCurrentScope.js';
import { useTeamMembers } from '../../team/hooks/useTeam.js';
import type { NoteItem, NoteVisibility } from '../api/notes.client.js';
import { useUpdateNoteMutation } from '../hooks/useNotes.js';

interface NoteShareModalProps {
  open: boolean;
  onClose: () => void;
  note: NoteItem;
}

export function NoteShareModal({ open, onClose, note }: NoteShareModalProps): ReactElement {
  useI18nSubscription();
  const currentUser = useCurrentUser();
  const { data: members } = useTeamMembers();
  const updateMut = useUpdateNoteMutation();

  // Автор — владелец доступа. Определяем по userId: `createdByMember` приходит
  // с сервера вместе с заметкой, отдельного запроса не нужно.
  const own = note.createdByMember?.userId === currentUser?.id;

  // Опции собираются при рендере: подписка useI18nSubscription перерисует
  // компонент при смене локали, а модульные константы остались бы старыми.
  const visibilityOptions: Array<{ value: NoteVisibility; label: string }> = [
    {
      value: 'workspace',
      label: i18n.t('crm.features.features.notes.components.notesharemodal.всем_в_пространстве'),
    },
    {
      value: 'own',
      label: i18n.t('crm.features.features.notes.components.notesharemodal.только_я'),
    },
    {
      value: 'shared',
      label: i18n.t('crm.features.features.notes.components.notesharemodal.выбранные_участники'),
    },
  ];

  const [visibility, setVisibility] = useState<NoteVisibility>(note.visibility ?? 'workspace');
  const [sharedWith, setSharedWith] = useState<string[]>(
    (note.shares ?? []).map((share) => share.memberId),
  );

  useEffect(() => {
    if (!open) return;
    updateMut.reset();
    setVisibility(note.visibility ?? 'workspace');
    setSharedWith((note.shares ?? []).map((share) => share.memberId));
    // `updateMut` в deps не включаем: React Query пересоздаёт объект мутации
    // при каждом notify, и reset → notify → re-render зацикливался бы.
  }, [open, note]);

  // Точечный список применим только при `shared`; в остальных режимах его не
  // трогаем, чтобы переключение туда-обратно не стирало выданные доступы.
  const save = (): void => {
    updateMut.mutate(
      {
        id: note.id,
        input: {
          visibility,
          ...(visibility === 'shared' ? { sharedWithMemberIds: sharedWith } : {}),
        },
      },
      { onSuccess: () => onClose() },
    );
  };

  // Автора исключаем: он и так видит своё, сервер отклонит выдачу самому себе.
  const authorMemberId = note.createdByMember?.id;
  const memberOptions = (members ?? [])
    .filter((member) => member.status === 'active' && member.id !== authorMemberId)
    .map((member) => ({ value: member.id, label: member.name || member.email }));

  return (
    <Modal
      title={i18n.t('crm.features.features.notes.components.notesharemodal.поделиться_заметкой')}
      open={open}
      onCancel={onClose}
      onOk={save}
      okText={i18n.t('crm.features.features.notes.components.notesharemodal.сохранить')}
      cancelText={i18n.t('crm.features.features.notes.components.notesharemodal.отмена')}
      okButtonProps={{ disabled: !own }}
      confirmLoading={updateMut.isPending}
      width={520}
      destroyOnHidden
    >
      {updateMut.isError ? (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          title={i18n.t(
            'crm.features.features.notes.components.notesharemodal.не_удалось_сохранить_доступ',
          )}
          description={(updateMut.error as Error).message}
        />
      ) : null}
      {!own ? (
        <Alert
          type="info"
          showIcon
          className="mb-4"
          title={i18n.t('crm.features.features.notes.components.notesharemodal.не_ваша_заметка')}
        />
      ) : null}
      <div className="flex flex-col gap-4">
        <div>
          <div className="mb-1 text-sm font-medium">
            {i18n.t('crm.features.features.notes.components.notesharemodal.видимость')}
          </div>
          <div className="mb-2 text-xs text-gray-500">
            {i18n.t('crm.features.features.notes.components.notesharemodal.кто_видит')}
          </div>
          <Radio.Group
            value={visibility}
            onChange={(e) => setVisibility(e.target.value as NoteVisibility)}
            options={visibilityOptions}
            disabled={!own}
          />
        </div>
        {visibility === 'shared' ? (
          <div>
            <div className="mb-1 text-sm font-medium">
              {i18n.t('crm.features.features.notes.components.notesharemodal.участники')}
            </div>
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              placeholder={i18n.t(
                'crm.features.features.notes.components.notesharemodal.выберите_участников',
              )}
              value={sharedWith}
              onChange={setSharedWith}
              options={memberOptions}
              optionFilterProp="label"
              disabled={!own}
            />
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
